import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import {
  type ConnectedInstagramIdentity,
  fetchConnectedInstagramIdentity,
  isCommentFromConnectedAccount,
} from "@/lib/instagram-comment-filters";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { InstagramPostThumbnail } from "./InstagramPostThumbnail";
import { resolveInstagramPostThumbnailUrls } from "@/lib/instagram-post-thumbnail";
import { Instagram, MessageSquare, ExternalLink, Send, Trash2, Mail } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PUBLIC_MAX_CHARS = 300;
const DM_MAX_CHARS = 300;
const DM_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function isDmWindowExpired(comment: InstagramComment): boolean {
  if (comment.dm_error === "dm_window_expired") return true;
  const createdMs = Date.parse(comment.created_at);
  if (Number.isNaN(createdMs)) return false;
  return Date.now() - createdMs > DM_WINDOW_MS;
}

function charCountLabel(length: number, max: number): string {
  return `${length}/${max}`;
}

interface InstagramPost {
  id: string;
  media_id: string;
  media_url: string | null;
  thumbnail_url: string | null;
  storage_thumbnail_url: string | null;
  storage_thumbnail_object_key: string | null;
  caption: string | null;
  permalink: string | null;
  created_at: string;
  comments_count?: number;
}

interface InstagramComment {
  id: string;
  comment_id: string;
  text: string;
  from_user_id: string;
  from_username: string | null;
  is_replied: boolean;
  is_dm_sent: boolean;
  dm_error: string | null;
  reply_mode: string | null;
  replied_at: string | null;
  reply_text: string | null;
  dm_text: string | null;
  created_at: string;
  lead: {
    id: string;
    name: string;
    metadata: any;
  } | null;
}

function commentStatusBadge(comment: InstagramComment) {
  if (comment.is_dm_sent && comment.is_replied) {
    return <Badge className="text-[10px]">DM + público</Badge>;
  }
  if (comment.is_dm_sent) {
    return <Badge variant="secondary" className="text-[10px]">DM enviada</Badge>;
  }
  if (comment.is_replied) {
    return <Badge variant="outline" className="text-[10px]">Só público</Badge>;
  }
  if (comment.dm_error) {
    return <Badge variant="destructive" className="text-[10px]">DM falhou</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] text-muted-foreground">Pendente</Badge>;
}

interface InstagramCommentsViewProps {
  workspaceId: string;
}

export function InstagramCommentsView({ workspaceId }: InstagramCommentsViewProps) {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<InstagramPost | null>(null);
  const [comments, setComments] = useState<InstagramComment[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [dmReplyingTo, setDmReplyingTo] = useState<string | null>(null);
  const [dmText, setDmText] = useState("");
  const [sendingDm, setSendingDm] = useState(false);
  const [deletingComment, setDeletingComment] = useState<string | null>(null);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const connectedIgIdentityRef = useRef<ConnectedInstagramIdentity | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPosts();

    // Setup realtime subscription for new comments
    const channelName = "instagram-comments-realtime";
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "INSERT",
        schema: "public",
        table: "instagram_comments",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => {
        fetchPosts();
        if (selectedPost) {
          fetchComments(selectedPost.id);
        }
      },
    );

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [workspaceId]);

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const identity = await fetchConnectedInstagramIdentity(supabase, workspaceId);
      connectedIgIdentityRef.current = identity;

      // Fetch posts with comment counts (exclui comentários da própria conta conectada)
      const { data: postsData, error } = await supabase
        .from("instagram_posts")
        .select(`
          id,
          media_id,
          media_url,
          thumbnail_url,
          storage_thumbnail_url,
          storage_thumbnail_object_key,
          caption,
          permalink,
          created_at
        `)
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (postsData) {
        const postsWithCounts = await Promise.all(
          postsData.map(async (post) => {
            const { data: commentRows } = await supabase
              .from("instagram_comments")
              .select("from_user_id, from_username")
              .eq("post_id", post.id);

            const visibleCount = (commentRows || []).filter(
              (r) => !isCommentFromConnectedAccount(r, identity),
            ).length;

            return {
              ...post,
              comments_count: visibleCount,
            };
          }),
        );

        setPosts(postsWithCounts);
        const presigned = await resolveInstagramPostThumbnailUrls(workspaceId, postsWithCounts);
        setThumbnailUrls(presigned);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchComments = async (postId: string) => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from("instagram_comments")
        .select(`
          id,
          comment_id,
          text,
          from_user_id,
          from_username,
          is_replied,
          is_dm_sent,
          dm_error,
          reply_mode,
          replied_at,
          reply_text,
          dm_text,
          created_at,
          lead:lead_id (
            id,
            name,
            metadata
          )
        `)
        .eq("post_id", postId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const identity =
        connectedIgIdentityRef.current ??
        (await fetchConnectedInstagramIdentity(supabase, workspaceId));
      connectedIgIdentityRef.current = identity;

      const rows = (data as InstagramComment[]) || [];
      setComments(rows.filter((c) => !isCommentFromConnectedAccount(c, identity)));
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSelectPost = (post: InstagramPost) => {
    setSelectedPost(post);
    setReplyingTo(null);
    setReplyText("");
    setDmReplyingTo(null);
    setDmText("");
    fetchComments(post.id);
  };

  const handleReply = async (commentId: string) => {
    if (!replyText.trim()) return;

    setSendingReply(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-instagram-comment-reply", {
        body: { commentId, message: replyText.trim().slice(0, PUBLIC_MAX_CHARS) },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Resposta enviada",
        description: "Sua resposta foi publicada com sucesso.",
      });

      setReplyingTo(null);
      setReplyText("");
      fetchComments(selectedPost!.id);
    } catch (error: any) {
      toast({
        title: "Erro ao responder",
        description: error.message || "Não foi possível enviar a resposta.",
        variant: "destructive",
      });
    } finally {
      setSendingReply(false);
    }
  };

  const handleSendDm = async (commentId: string) => {
    if (!dmText.trim()) return;

    setSendingDm(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-instagram-private-reply", {
        body: { commentId, message: dmText.trim().slice(0, DM_MAX_CHARS) },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: data?.alreadySent ? "DM já enviada" : "DM enviada",
        description: data?.alreadySent
          ? "Este comentário já tinha uma resposta privada registrada."
          : "A mensagem privada foi enviada com sucesso.",
      });

      setDmReplyingTo(null);
      setDmText("");
      fetchComments(selectedPost!.id);
    } catch (error: any) {
      toast({
        title: "Erro ao enviar DM",
        description: error.message || "Não foi possível enviar a mensagem privada.",
        variant: "destructive",
      });
    } finally {
      setSendingDm(false);
    }
  };

  /** Remove o post e comentários só do banco local; não chama API do Instagram. */
  const handleRemovePostFromSystem = async (postId: string) => {
    setDeletingPostId(postId);
    try {
      const { error } = await supabase
        .from("instagram_posts")
        .delete()
        .eq("id", postId)
        .eq("workspace_id", workspaceId);

      if (error) throw error;

      toast({
        title: "Publicação removida da lista",
        description: "Ela continua no Instagram; apenas sumiu deste painel.",
      });

      setPosts((prev) => prev.filter((p) => p.id !== postId));
      if (selectedPost?.id === postId) {
        setSelectedPost(null);
        setComments([]);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Não foi possível remover.";
      toast({
        title: "Erro ao remover",
        description: message,
        variant: "destructive",
      });
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    setDeletingComment(commentId);
    try {
      const { error } = await supabase.functions.invoke("delete-instagram-comment", {
        body: { commentId },
      });

      if (error) throw error;

      toast({
        title: "Comentário excluído",
        description: "O comentário foi removido com sucesso.",
      });

      fetchComments(selectedPost!.id);
      fetchPosts(); // Atualiza contagem
    } catch (error: any) {
      toast({
        title: "Erro ao excluir",
        description: error.message || "Não foi possível excluir o comentário.",
        variant: "destructive",
      });
    } finally {
      setDeletingComment(null);
    }
  };

  if (loadingPosts) {
    return (
      <div className="flex h-full">
        <div className="w-80 border-r p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <div className="flex-1 p-4">
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Instagram className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Nenhum comentário ainda</h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          Quando usuários comentarem em suas publicações, eles aparecerão aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Lista de Posts */}
      <ScrollArea className="w-80 border-r">
        <div className="p-2">
          {posts.map((post) => (
            <div
              key={post.id}
              onClick={() => handleSelectPost(post)}
              className={`p-3 rounded-lg cursor-pointer transition-colors mb-2 ${
                selectedPost?.id === post.id
                  ? "bg-primary/10 border border-primary/30"
                  : "hover:bg-muted/50 border border-transparent"
              }`}
            >
              <div className="flex gap-2 items-start">
                <InstagramPostThumbnail post={post} presignedByPostId={thumbnailUrls} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm line-clamp-2 mb-1">
                    {post.caption || "Sem legenda"}
                  </p>
                  <Badge variant="outline" className="text-xs">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    {post.comments_count || 0}
                  </Badge>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={deletingPostId === post.id}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Remover publicação desta lista"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover da lista?</AlertDialogTitle>
                      <AlertDialogDescription>
                        A publicação e os comentários salvos aqui serão apagados só neste sistema. Nada é
                        excluído no Instagram.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleRemovePostFromSystem(post.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Remover da lista
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Comentários do Post Selecionado */}
      <div className="flex-1 flex flex-col min-h-0">
        {selectedPost ? (
          <>
            {/* Header do Post */}
            <div className="p-4 border-b flex items-start gap-4 flex-shrink-0">
              <InstagramPostThumbnail
                post={selectedPost}
                presignedByPostId={thumbnailUrls}
                size="md"
                className="w-20 h-20"
              />
              <div className="flex-1 min-w-0 flex gap-2 items-start">
                <div className="flex-1 min-w-0">
                  <p className="font-medium line-clamp-2 mb-2">
                    {selectedPost.caption || "Sem legenda"}
                  </p>
                  {selectedPost.permalink && (
                    <a
                      href={selectedPost.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Ver no Instagram
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 flex-shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={deletingPostId === selectedPost.id}
                      aria-label="Remover publicação desta lista"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover da lista?</AlertDialogTitle>
                      <AlertDialogDescription>
                        A publicação e os comentários salvos aqui serão apagados só neste sistema. Nada é
                        excluído no Instagram.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleRemovePostFromSystem(selectedPost.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Remover da lista
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            {/* Lista de Comentários */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {loadingComments ? (
                  <>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-24 w-full" />
                    ))}
                  </>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhum comentário nesta publicação</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage
                            src={comment.lead?.metadata?.instagram_profile_pic}
                          />
                          <AvatarFallback>
                            {comment.from_username?.[0]?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                @{comment.from_username || "usuário"}
                              </span>
                              {commentStatusBadge(comment)}
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.created_at), {
                                  addSuffix: true,
                                  locale: ptBR,
                                })}
                              </span>
                            </div>
                            
                            {/* Botão de excluir */}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  disabled={deletingComment === comment.id}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir comentário?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação irá remover o comentário da sua lista. Se for um comentário seu ou em um post seu, ele também será removido do Instagram.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                          <p className="text-sm">{comment.text}</p>

                          {/* Resposta existente */}
                          {comment.is_replied && comment.reply_text && (
                            <div className="mt-3 pl-4 border-l-2 border-primary/30">
                              <p className="text-xs font-medium text-primary mb-1">
                                Resposta pública:
                              </p>
                              <p className="text-sm">{comment.reply_text}</p>
                            </div>
                          )}

                          {comment.is_dm_sent && comment.dm_text && (
                            <div className="mt-3 pl-4 border-l-2 border-violet-400/40">
                              <p className="text-xs font-medium text-violet-600 dark:text-violet-400 mb-1">
                                DM (Private Reply):
                              </p>
                              <p className="text-sm">{comment.dm_text}</p>
                            </div>
                          )}

                          {comment.dm_error && !comment.is_dm_sent && (
                            <p className="mt-2 text-xs text-destructive">
                              Erro na DM: {comment.dm_error === "dm_window_expired"
                                ? "janela de 7 dias expirada"
                                : comment.dm_error}
                            </p>
                          )}

                          {/* Resposta pública manual */}
                          {!comment.is_replied && (
                            <div
                              className={`mt-3 rounded-md p-3 ${
                                !comment.is_dm_sent && !comment.dm_error
                                  ? "bg-muted/40 border border-border"
                                  : comment.dm_error
                                    ? "bg-amber-500/5 border border-amber-500/30"
                                    : "bg-muted/30 border border-dashed border-border"
                              }`}
                            >
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                {comment.dm_error
                                  ? "Resposta pública ainda pendente — envie manualmente se necessário"
                                  : comment.is_dm_sent
                                    ? "Complementar com resposta pública"
                                    : "Resposta pública"}
                              </p>
                              {replyingTo === comment.id ? (
                                <div className="space-y-1">
                                  <div className="flex gap-2">
                                    <Input
                                      value={replyText}
                                      onChange={(e) =>
                                        setReplyText(e.target.value.slice(0, PUBLIC_MAX_CHARS))
                                      }
                                      placeholder="Escreva sua resposta pública..."
                                      className="text-sm"
                                      maxLength={PUBLIC_MAX_CHARS}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                          e.preventDefault();
                                          handleReply(comment.id);
                                        }
                                      }}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleReply(comment.id)}
                                      disabled={sendingReply || !replyText.trim()}
                                    >
                                      <Send className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setReplyingTo(null);
                                        setReplyText("");
                                      }}
                                    >
                                      Cancelar
                                    </Button>
                                  </div>
                                  <p
                                    className={`text-xs ${
                                      replyText.length >= PUBLIC_MAX_CHARS
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {charCountLabel(replyText.length, PUBLIC_MAX_CHARS)}
                                  </p>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant={comment.dm_error ? "default" : "outline"}
                                  onClick={() => {
                                    setReplyingTo(comment.id);
                                    setDmReplyingTo(null);
                                  }}
                                >
                                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                                  Responder publicamente
                                </Button>
                              )}
                            </div>
                          )}

                          {/* DM manual */}
                          {!comment.is_dm_sent && !isDmWindowExpired(comment) && (
                            <div className="mt-3 rounded-md p-3 bg-violet-500/5 border border-violet-500/20">
                              <p className="text-xs font-medium text-violet-700 dark:text-violet-300 mb-2">
                                {comment.dm_error ? "Tentar enviar DM novamente" : "Enviar DM (Private Reply)"}
                              </p>
                              {dmReplyingTo === comment.id ? (
                                <div className="space-y-1">
                                  <div className="flex gap-2">
                                    <Input
                                      value={dmText}
                                      onChange={(e) =>
                                        setDmText(e.target.value.slice(0, DM_MAX_CHARS))
                                      }
                                      placeholder="Mensagem privada para o comentarista..."
                                      className="text-sm"
                                      maxLength={DM_MAX_CHARS}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                          e.preventDefault();
                                          handleSendDm(comment.id);
                                        }
                                      }}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleSendDm(comment.id)}
                                      disabled={sendingDm || !dmText.trim()}
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setDmReplyingTo(null);
                                        setDmText("");
                                      }}
                                    >
                                      Cancelar
                                    </Button>
                                  </div>
                                  <p
                                    className={`text-xs ${
                                      dmText.length >= DM_MAX_CHARS
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {charCountLabel(dmText.length, DM_MAX_CHARS)}
                                  </p>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-violet-500/30 text-violet-700 dark:text-violet-300"
                                  onClick={() => {
                                    setDmReplyingTo(comment.id);
                                    setReplyingTo(null);
                                  }}
                                >
                                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                                  Enviar DM
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Instagram className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Selecione uma publicação para ver os comentários</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
